/**
 * Textes de la gouvernance du catalogue (2026-10-19, contrat 929e8437 ; français, langue de
 * référence) : fiche de table (onglets, actions, extraits, synchronisation) et glossaire métier.
 * Branchés dans `fr.ts` ; `catalogue.en.ts` porte exactement les mêmes clés (typage + `i18n.test.ts`).
 *
 * Vocabulaire : le « layer » du contrat est un **catalogue** pour l'utilisateur (Unity Catalog,
 * Trino : catalogue > schéma > table) ; la base garde son nom.
 */
export const catalogTableFr = {
  tabs: {
    overview: 'Vue d’ensemble',
    schema: 'Schéma',
    preview: 'Aperçu',
    statistics: 'Statistiques',
    history: 'Historique',
    lineage: 'Lignage',
    usage: 'Usages',
  },
  noDescription: 'Aucune description. Ajoutez-en une dans l’onglet Définition, ou générez-la.',
  overview: {
    qualifiedName: 'Nom moteur',
    quality: 'Qualité',
    status: 'Statut',
    properties: 'Propriétés du moteur',
    certifiedBy: 'par {{by}}, le {{date}}',
  },
  status: { active: 'Active', dropped: 'Disparue du moteur' },
  dropped: {
    title: 'Table disparue du moteur',
    description:
      'Le catalogue Iceberg ne la connaît plus depuis le {{date}}. Sa gouvernance (propriétaire, tags, glossaire, certification) est conservée.',
  },
  noEngine: {
    title: 'Table sans référence moteur',
    description:
      'Cette table est déclarée au catalogue, mais aucun moteur ne la connaît : ni aperçu, ni statistiques, ni historique.',
    short: 'Aucun (table déclarée à la main)',
  },
  engineMissing: {
    title: 'Le moteur ne connaît plus cette table',
    description:
      'Le catalogue Iceberg ne la trouve pas. Resynchronisez le catalogue pour mettre la fiche à jour.',
  },
  governance: {
    owner: 'Propriétaire',
    ownerHelp: 'Un utilisateur ou un groupe du locataire, responsable de la table.',
    noOwner: 'Aucun propriétaire',
    groupLabel: 'Groupe {{name}}',
    principal: { user: 'Utilisateur', group: 'Groupe' },
    stewards: 'Stewards',
    stewardsHelp: 'Une entrée par steward : user:<identifiant> ou group:<identifiant>.',
    tags: 'Tags',
    tagsHelp: 'Minuscules, chiffres et « _ . : - », 64 caractères au plus ; {{max}} tags au plus.',
    certification: 'Certification',
    certificationNote: 'Note de certification',
    glossary: 'Termes du glossaire',
    glossaryHelp: 'Un terme par ligne, par son nom. Termes connus : {{terms}}.',
    glossaryEmpty: 'Le glossaire est vide : créez d’abord des termes dans Glossaire.',
    classificationHelp: 'Liste fermée, lue par les futures politiques du moteur (masquage, filtres).',
    editTitle: 'Gouvernance de la table',
    editDescription: 'Propriétaire, stewards, tags, certification et termes du glossaire.',
  },
  certification: { certified: 'Certifiée', deprecated: 'Dépréciée', none: 'Non certifiée' },
  classification: {
    none: 'Non classée',
    public: 'Publique',
    internal: 'Interne',
    confidential: 'Confidentielle',
    sensitive: 'Sensible',
    pii: 'Donnée personnelle',
  },
  columns: {
    description: 'Description',
    classification: 'Classification',
    edit: 'Modifier la colonne {{name}}',
    editTitle: 'Colonne {{name}}',
    editDescription: 'Description, classification et tags de la colonne.',
  },
  preview: {
    meta_one: '{{count}} ligne de {{name}}',
    meta_other: '{{count}} lignes de {{name}}',
    truncated: '{{limit}} premières lignes',
    empty: { title: 'Table vide', description: 'Le moteur ne renvoie aucune ligne pour cette table.' },
    unavailable: {
      title: 'Aperçu indisponible',
      description: 'L’aperçu passe par l’entrepôt Trino du locataire, qui n’est pas configuré ici.',
    },
  },
  statistics: {
    records: 'Lignes',
    files: 'Fichiers',
    size: 'Taille',
    deleteFiles: 'Fichiers de suppression',
    partitionSpec: 'Partitionnement',
    unpartitioned: 'Non partitionnée',
    sortOrder: 'Ordre de tri',
    unsorted: 'Aucun',
    snapshot: 'Snapshot',
    computedAt: 'Calculées le',
    partial: 'Métriques de colonnes lues sur les {{files}} premiers fichiers de données seulement.',
    columnsTitle: 'Statistiques des colonnes',
    columns: {
      name: 'Colonne',
      nulls: 'Nulles',
      distinct: 'Distinctes',
      min: 'Min',
      max: 'Max',
      size: 'Taille',
    },
    noColumns: {
      title: 'Aucune statistique de colonne',
      description: 'Les fichiers de données ne portent pas de métriques de colonne.',
    },
  },
  history: {
    subtitle_one: '{{count}} snapshot, du plus récent au plus ancien.',
    subtitle_other: '{{count}} snapshots, du plus récent au plus ancien.',
    current: 'Courant',
    summary:
      '+{{added}} lignes · −{{deleted}} lignes · {{total}} lignes au total · {{files}} fichiers · {{size}}',
    operations: { append: 'Ajout', overwrite: 'Réécriture', replace: 'Remplacement', delete: 'Suppression' },
    empty: { title: 'Aucun snapshot', description: 'La table n’a encore reçu aucune écriture.' },
  },
  lineage: {
    subtitle: 'Jeu OpenLineage {{name}} ({{namespace}}).',
    empty: {
      title: 'Aucun lignage connu pour cette table',
      description: 'Aucun run n’a encore lu ni écrit ce jeu en émettant des événements OpenLineage.',
    },
  },
  usage: {
    queries: 'Requêtes sur 7 jours',
    users: 'Utilisateurs sur 7 jours',
    lastQueried: 'Dernière requête',
    soon: {
      title: 'Usages : bientôt',
      description:
        'Qui interroge cette table, et à quelle fréquence : la mesure arrive avec l’écoute des requêtes Trino.',
    },
  },
  actions: {
    query: 'Requêter dans l’éditeur SQL',
    queryShort: 'Requêter en SQL',
    use: 'Utiliser',
    queryLoading: 'Recherche de l’entrepôt Trino…',
    queryNoWarehouse: 'Aucun entrepôt Trino dans ce locataire.',
    notebook: 'Ouvrir dans un notebook',
    snippets: 'Copier un extrait',
    governance: 'Gouvernance',
  },
  snippets: {
    title: 'Lire cette table',
    description: 'Le même nom, {{name}}, depuis chaque moteur de la plateforme.',
    kinds: {
      trino: 'Trino SQL',
      spark: 'Spark',
      python: 'Python',
      dask: 'Dask',
      duckdb: 'DuckDB',
      jdbc: 'JDBC',
    },
    languages: { sql: 'SQL', python: 'Python', jdbc: 'JDBC' },
    hints: {
      trino: 'Dans l’éditeur SQL de la console ou tout client branché sur le Trino du locataire.',
      spark: 'Dans un run Spark de graal : le catalogue graal est posé par la plateforme.',
      python: 'Dans un run Python, Ray ou dbt, ou une session : module graal, lecture en pandas.',
      dask: 'Dans un run Dask : une partition par fichier de données, lue par les workers.',
      duckdb: 'Dans un run ou une session Python : la table pyiceberg chargée dans DuckDB.',
      jdbc: 'Pilote Trino JDBC. Trino n’est pas exposé hors de la plateforme : l’hôte vient de l’administrateur.',
    },
  },
  notebook: {
    title: 'Ouvrir dans un notebook',
    description:
      'Choisissez une session Python : sa cellule est préremplie avec la lecture de la table, à exécuter vous-même.',
    sessions: 'Sessions',
    starting: 'démarre',
    newSession: 'Nouvelle session',
  },
  sync: {
    title: 'Synchronisation avec le catalogue Iceberg',
    run: 'Resynchroniser',
    loading: 'Lecture de la dernière synchronisation…',
    never: 'Jamais synchronisé.',
    summary:
      'Dernière synchronisation {{when}} ({{trigger}}) : {{seen}} tables vues, {{created}} créées, {{updated}} mises à jour, {{dropped}} disparues.',
    truncated: 'Liste tronquée : aucune table n’a été marquée disparue.',
    unavailable: 'Indisponible : aucun catalogue Iceberg n’est configuré sur cette plateforme.',
    queued: 'Une synchronisation tourne déjà : la vôtre la suivra.',
    done: 'Catalogue synchronisé : {{created}} tables ajoutées, {{dropped}} disparues.',
    failed: 'La synchronisation a échoué : {{detail}}',
    statuses: {
      never: 'Jamais',
      running: 'En cours',
      queued: 'En file',
      completed: 'Terminée',
      failed: 'En échec',
      disabled: 'Indisponible',
    },
    triggers: { manual: 'manuelle', schedule: 'planifiée', run: 'fin de run', event: 'événement' },
  },
} as const;

export const glossaryFr = {
  title: 'Glossaire',
  subtitle: 'Le vocabulaire métier de l’organisation, rattaché aux tables et aux colonnes du catalogue.',
  searchPlaceholder: 'Rechercher un terme, un synonyme…',
  new: 'Nouveau terme',
  newTitle: 'Nouveau terme',
  newSubtitle: 'Un nom unique, une définition, des synonymes. Le rattachement se fait depuis la table.',
  detailSubtitle: 'Terme du glossaire métier',
  created: 'Terme « {{name}} » créé.',
  duplicate: 'Un terme porte déjà ce nom.',
  deleted: 'Terme « {{name}} » supprimé.',
  deleteFailed: 'La suppression a échoué : {{detail}}',
  delete: 'Supprimer le terme',
  confirmDelete:
    'Supprimer le terme « {{name}} » ? Ses liens vers les tables et les colonnes partent avec lui.',
  columns: {
    name: 'Terme',
    synonyms: 'Synonymes',
    description: 'Définition',
    owner: 'Propriétaire',
    updated: 'Modifié',
  },
  empty: {
    title: 'Glossaire vide',
    description:
      'Définissez les termes métier (point de livraison, consommation…) pour les rattacher aux tables.',
  },
  fields: {
    name: 'Nom',
    namePlaceholder: 'Point de livraison',
    description: 'Définition',
    descriptionHelp: 'Ce que le terme veut dire pour le métier, en une ou deux phrases.',
    synonyms: 'Synonymes',
    synonymsHelp: 'Un synonyme par ligne ; la recherche les lit aussi.',
    owner: 'Propriétaire',
  },
  tabs: { summary: 'Résumé', assets: 'Actifs liés', definition: 'Définition' },
  assets: {
    title: 'Actifs liés',
    description: 'Les tables et les colonnes qui portent ce terme, parmi celles que vous pouvez lire.',
    columns: { asset: 'Actif', kind: 'Nature', location: 'Catalogue / base', qualifiedName: 'Nom moteur' },
    kinds: { table: 'Table', field: 'Colonne' },
    empty: {
      title: 'Aucun actif lié',
      description: 'Rattachez ce terme à une table depuis sa fiche (Gouvernance).',
    },
  },
} as const;
